import React, { useEffect, useState } from 'react';

import * as AntIcons from '@ant-design/icons';
import { Drawer, Flex, Image, Input, Layout, Menu } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import eCoachVMSApi from '../../Api';
import LogoIcon from '../../images/logo-icon.png';
import LogoMain from '../../images/Logo.png';
import useScreenSize from '../../utils/useScreenSize';
import { selectPermissions, selectUser } from '../../store/reducers/sessionReducer';
import './sidebar.css';
import { useSelector } from 'react-redux';
import routeConfig from '../../RouteConfig';

const { Sider } = Layout;
const { Search } = Input;

const Sidebar = ({ collapsed, open, onClose }) => {
    const { baseURL } = eCoachVMSApi();
    const navigate = useNavigate();
    const { isMobile, isTablet } = useScreenSize();
    const location = useLocation();
    const [menuItems, setMenuItems] = useState([]);
    const [openKeys, setOpenKeys] = useState([]);
    const permissions = useSelector(selectPermissions);
    const user = useSelector(selectUser);

    // Function to render icons - handles both special cases and dynamic icons
    const renderIcon = (iconName) => {
        if (!iconName) return null;

        // Define special icon mappings
        const specialIcons = {
            'anticon-home': <AntIcons.HomeOutlined />,
            'anticon-calendar': <AntIcons.CalendarOutlined />,
            'anticon-schedule': <AntIcons.ScheduleOutlined />,
            'anticon-exclamation-circle': <AntIcons.ExclamationCircleOutlined />,
            'anticon-file-text': <AntIcons.FileTextOutlined />,
            'anticon-file-done': <AntIcons.FileDoneOutlined />,
            'anticon-tool': <AntIcons.ToolOutlined />,
            'anticon-toolbox': <AntIcons.AppstoreOutlined />,
            'anticon-appstore': <AntIcons.AppstoreOutlined />,
            'anticon-setting': <AntIcons.SettingOutlined />
        };

        // If it's a special icon, return it
        if (specialIcons[iconName]) {
            return specialIcons[iconName];
        }

        // Try to render it as a dynamic icon
        const IconComponent = AntIcons[iconName];
        return IconComponent ? <IconComponent /> : null;
    };

    const buildMenuTree = (items) => {
        const itemMap = new Map();
        const menuTree = [];

        // Map all items by their IDs
        items.forEach((item) => {
            itemMap.set(item.id, { ...item, children: [] });
        });

        // Build the tree structure
        items.forEach((item) => {
            if (item.parent_id === null || item.parent_id === 0) {
                menuTree.push(itemMap.get(item.id));
            } else {
                const parent = itemMap.get(item.parent_id);
                if (parent) {
                    parent.children.push(itemMap.get(item.id));
                }
            }
        });

        // Sort menu items by sort_order if available
        const sortItems = (items) => {
            items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            items.forEach((item) => {
                if (item.children && item.children.length > 0) {
                    sortItems(item.children);
                }
            });
        };

        sortItems(menuTree);
        return menuTree;
    };

    // Fetch menu data from API
    useEffect(() => {
        const fetchMenu = async () => {
            try {
                if (permissions?.length) {
                    const response = await baseURL.get('/getMenu');
                    const data = response.data;
                    const menuTree = buildMenuTree(data);
                    setMenuItems(menuTree);
                    const openSubMenus = getOpenKeysFromPath(location.pathname, menuTree);
                    setOpenKeys(openSubMenus);
                } else {
                    setMenuItems([]);
                    setOpenKeys([]);
                }
            } catch (error) {
                console.error('Error fetching menu:', error);
                setMenuItems([]);
                setOpenKeys([]);
            }
        };
        fetchMenu();
    }, [permissions]);

    // Get open submenu keys based on current path
    const getOpenKeysFromPath = (path, menuTree) => {
        const keys = [];
        const findOpenKey = (itemsToSearch) => {
            itemsToSearch.forEach((item) => {
                // Check if current item's path is a prefix of the location path
                if (path.startsWith(item.react_path) && item.children?.length) {
                    keys.push(item.react_path); // Add parent if it's part of the active path
                }
                // If item has children, recurse
                if (item.children?.length) {
                    const childMatch = item.children.some((child) => path.startsWith(child.react_path));
                    if (childMatch) {
                        keys.push(item.react_path); // Open parent if a child matches
                    }
                    findOpenKey(item.children);
                }
            });
        };
        findOpenKey(menuTree);
        return [...new Set(keys)];
    };

    const handleMenuClick = ({ key }) => {
        const apiUrl = process.env.REACT_APP_API_URL;

        if (key.includes('[REACT_APP_API_URL]')) {
            // Replace placeholder with actual API URL
            const finalUrl = key.replace('[REACT_APP_API_URL]', apiUrl);
            // Open in a new tab
            window.open(finalUrl, '_blank', 'noopener,noreferrer');
        } else {
            // Normal internal navigation
            navigate(key);
        }

        if ((isMobile || isTablet) && onClose) onClose();
    };

    // Recursively render menu items with permission checks
    const renderMenuItems = (items) => {
        return items
            .map((item) => {
                let isVisible = item.is_active !== 0;

                if (isVisible) {
                    const routeInfo = routeConfig.find((route) => route.path === item.react_path);
                    const requiredPermission = routeInfo?.auth?.permission;

                    if (requiredPermission) {
                        if (!permissions?.length || !permissions.includes(requiredPermission)) {
                            isVisible = false;
                        }
                    }
                    // If routeInfo is not found or no permission is specified, it's considered accessible
                    // (unless item.is_active was false or other rules applied)
                }

                if (!isVisible) {
                    return null;
                }

                if (item.children && item.children.length > 0) {
                    const renderedChildren = renderMenuItems(item.children);
                    const visibleChildren = renderedChildren.filter(Boolean);

                    if (visibleChildren.length === 0) {
                        return null;
                    }

                    const isActivePath = location.pathname === item.react_path || location.pathname.startsWith(`${item.react_path}/`);
                    const isAnyChildActive = item.children.some((child) => {
                        const isChildVisible = visibleChildren.some((vc) => vc.key === child.react_path);
                        const isChildPathActive = location.pathname === child.react_path || location.pathname.startsWith(`${child.react_path}/`);
                        return isChildVisible && isChildPathActive;
                    });
                    const menuItemClass = isActivePath || isAnyChildActive ? 'menu-item-active' : '';

                    return (
                        <Menu.SubMenu key={item.react_path} title={item.name} icon={renderIcon(item.icon)} className={menuItemClass}>
                            {visibleChildren}
                        </Menu.SubMenu>
                    );
                }

                const isActivePath = location.pathname === item.react_path || location.pathname.startsWith(`${item.react_path}/`);
                const menuItemClass = isActivePath ? 'menu-item-active' : '';

                return (
                    <Menu.Item key={item.react_path} icon={renderIcon(item.icon)} className={menuItemClass}>
                        {item.name}
                    </Menu.Item>
                );
            })
            .filter(Boolean); // Filter out null items from the top-level mapping
    };

    // Render desktop sidebar
    const renderDesktopSidebar = () => (
        <Sider width={220} trigger={null} collapsible collapsed={collapsed} style={{ backgroundColor: '#f8fafb' }}>
            <Flex
                align="center"
                justify="center"
                className={`${collapsed ? 'min-h-[51px]' : 'max-h-[51px]'}`}
                style={{
                    padding: 5,
                    backgroundColor: '#3382b0',
                    textAlign: 'center'
                }}>
                {!collapsed ? (
                    <Link to="/">
                        <Image src={LogoMain} preview={false} style={{ height: '40px', objectFit: 'contain' }} />
                    </Link>
                ) : (
                    <Link to="/">
                        <Image src={LogoIcon} preview={false} style={{ height: '32px', objectFit: 'contain' }} />
                    </Link>
                )}
            </Flex>

            {!collapsed && (
                <div
                    className="sidemenu"
                    style={{
                        padding: '15px 12px',
                        marginBottom: 2,
                        borderBottom: '1px solid #dbdbdb'
                    }}>
                    <h3 className="name">Hello, {user?.name}</h3>
                    <Search placeholder="Search..." />
                </div>
            )}

            <Menu
                className="sidebar-menus deskto"
                mode="inline"
                onClick={handleMenuClick}
                style={{ backgroundColor: 'transparent', border: '0 none' }}
                openKeys={openKeys}
                onOpenChange={(keys) => {
                    // Find the key that was toggled
                    const toggledKey = keys.find((key) => !openKeys.includes(key));

                    if (toggledKey) {
                        // If the key is not in openKeys, it's a new open, so add it
                        setOpenKeys([...openKeys, toggledKey]);
                    } else {
                        // If no new key is toggled, it means one of the existing open keys was closed, so update accordingly
                        setOpenKeys(keys);
                    }
                }}>
                {renderMenuItems(menuItems)}
            </Menu>
        </Sider>
    );

    // Render mobile drawer
    const renderMobileDrawer = () => (
        <Drawer placement="left" width={280} onClose={onClose} open={open} className="mobile-drawer-sidebar">
            <Flex
                align="center"
                justify="center"
                style={{
                    padding: 5,
                    backgroundColor: '#3382b0',
                    textAlign: 'center',
                    minHeight: '51px'
                }}>
                <Image src={LogoMain} preview={false} style={{ height: '40px' }} />
            </Flex>

            <div
                style={{
                    padding: '15px 12px',
                    marginBottom: 2,
                    borderBottom: '1px solid #dbdbdb'
                }}>
                <h3 className="name">Hello, {user?.name}</h3>
                <Search placeholder="Search..." />
            </div>

            <Menu
                className="sidebar-menus"
                mode="inline"
                onClick={handleMenuClick}
                style={{ backgroundColor: 'transparent', border: '0 none' }}
                openKeys={openKeys}
                onOpenChange={(keys) => {
                    // Keep track of the newly opened keys while preserving the previously opened ones
                    const latestOpenKey = keys.find((key) => !openKeys.includes(key));
                    const newOpenKeys = latestOpenKey ? [...openKeys, latestOpenKey] : openKeys;

                    // If the parent menu is closing, ensure that we don't close its parent or grandparent
                    setOpenKeys(newOpenKeys);
                }}>
                {renderMenuItems(menuItems)}
            </Menu>
        </Drawer>
    );

    return <>{isMobile || isTablet ? renderMobileDrawer() : renderDesktopSidebar()}</>;
};

export default Sidebar;
